import React, { useEffect, useState } from 'react';
import RTSpinner from '../common/RTSpinner';
import RTButton from '../common/RTButton';
import RTModal from '../common/RTModal';
import RTPageLayout from '../common/RTPageLayout';
import { apiGet, apiPost } from '../../api/apiClient';
import { toastError } from '../common/toast';
import { formatDateToDDMMYYYY } from '../../utils/dateHelper';
import { hasPermission } from '../../utils/permissions';
import { formatINR, prettyStatus, statusBadgeClass } from './pgFormat';
import {
  confirmExternalPayment,
  openExternalPayment,
  useRefreshOnPaymentReturn,
} from './pgPaymentReturn';
import './PGPortal.css';

const PGInvoices = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [paying, setPaying] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiGet('/pg/me/invoices');
      setInvoices(res.data || []);
      setSelected(null);
    } catch (err) {
      toastError(err.response?.data?.detail || 'Unable to load invoices.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useRefreshOnPaymentReturn(load);

  const openDetail = async (invoice) => {
    setDetailLoading(true);
    setSelected(invoice);
    try {
      const res = await apiGet(`/pg/me/invoices/${invoice.invoice_id}`);
      setSelected(res.data);
    } catch (err) {
      toastError(
        err.response?.data?.detail || 'Unable to load invoice details.',
      );
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePay = async () => {
    if (!selected || !hasPermission('pg-invoices.pay')) {
      toastError('You do not have permission to pay online.');
      return;
    }
    const confirmed = await confirmExternalPayment();
    if (!confirmed) return;
    try {
      setPaying(true);
      const res = await apiPost(`/pg/me/invoices/${selected.invoice_id}/pay`);
      const url = res.data?.payment_url;
      if (!url) {
        toastError('Payment link unavailable.');
        return;
      }
      openExternalPayment(url);
    } catch (err) {
      toastError(err.response?.data?.detail || 'Failed to start payment.');
    } finally {
      setPaying(false);
    }
  };

  return (
    <RTPageLayout
      title="Invoices"
      subtitle="Your bills and balances"
      mainSection
    >
      <div className="pg-portal">
        {loading ? (
          <RTSpinner message="Loading invoices…" fullScreen={false} />
        ) : invoices.length === 0 ? (
          <div className="pg-empty">No invoices yet.</div>
        ) : (
          invoices.map((inv) => {
            const items = inv.invoice_items || [];
            return (
              <div
                key={inv.invoice_id}
                className="pg-card pg-invoice-card pg-list-item"
                onClick={() => openDetail(inv)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openDetail(inv)}
              >
                <div className="pg-invoice-card-top">
                  <div className="pg-invoice-card-heading">
                    <h3>{inv.invoice_number}</h3>
                    <div className="pg-invoice-card-meta">
                      {formatDateToDDMMYYYY(inv.billing_period_start)} –{' '}
                      {formatDateToDDMMYYYY(inv.billing_period_end)}
                      {inv.due_date
                        ? ` · Due ${formatDateToDDMMYYYY(inv.due_date)}`
                        : ''}
                    </div>
                  </div>
                  <span className={`pg-badge ${statusBadgeClass(inv.status)}`}>
                    {prettyStatus(inv.status)}
                  </span>
                </div>

                <div className="pg-invoice-card-items">
                  {items.length === 0 ? (
                    <div className="pg-muted">No line items</div>
                  ) : (
                    items.map((item) => (
                      <div
                        key={item.invoice_item_id}
                        className="pg-invoice-card-item"
                      >
                        <div className="pg-invoice-card-item-main">
                          <span className="pg-invoice-card-item-type">
                            {item.invoice_item_type}
                          </span>
                          {item.description ? (
                            <span className="pg-invoice-card-item-desc">
                              {item.description}
                            </span>
                          ) : null}
                        </div>
                        <span className="pg-invoice-card-item-amount">
                          {formatINR(item.total_amount)}
                        </span>
                      </div>
                    ))
                  )}
                </div>

                <div className="pg-invoice-card-total">
                  <span>Total</span>
                  <strong>{formatINR(inv.total_amount)}</strong>
                </div>
              </div>
            );
          })
        )}
      </div>

      <RTModal
        isOpen={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? `Invoice ${selected.invoice_number}` : 'Invoice'}
        size="medium"
      >
        {detailLoading || !selected ? (
          <RTSpinner message="Loading…" fullScreen={false} />
        ) : (
          <div className="pg-modal-body">
            <div className="pg-row">
              <span className="pg-row-label">Status</span>
              <span className="pg-row-value">
                {prettyStatus(selected.status)}
              </span>
            </div>
            <div className="pg-row">
              <span className="pg-row-label">Due</span>
              <span className="pg-row-value">
                {formatDateToDDMMYYYY(selected.due_date)}
              </span>
            </div>
            <div className="pg-row">
              <span className="pg-row-label">Total</span>
              <span className="pg-row-value">
                {formatINR(selected.total_amount)}
              </span>
            </div>
            <div className="pg-row">
              <span className="pg-row-label">Paid</span>
              <span className="pg-row-value">
                {formatINR(selected.amount_paid)}
              </span>
            </div>
            <div className="pg-row">
              <span className="pg-row-label">Remaining</span>
              <span className="pg-row-value">
                {formatINR(selected.amount_remaining)}
              </span>
            </div>

            <h4
              style={{
                margin: '16px 0 8px',
                color: '#153c7f',
                fontSize: '0.95rem',
              }}
            >
              Breakup
            </h4>
            {(selected.invoice_items || []).length === 0 ? (
              <div className="pg-empty">No line items.</div>
            ) : (
              (selected.invoice_items || []).map((item) => (
                <div key={item.invoice_item_id} className="pg-breakup-item">
                  <div className="pg-breakup-type">
                    {item.invoice_item_type}
                  </div>
                  {item.description ? (
                    <div className="pg-breakup-desc">{item.description}</div>
                  ) : null}
                  <div
                    className="pg-row"
                    style={{ borderBottom: 'none', paddingBottom: 0 }}
                  >
                    <span className="pg-row-label">Amount</span>
                    <span className="pg-row-value">
                      {formatINR(item.total_amount)}
                    </span>
                  </div>
                </div>
              ))
            )}

            {selected.can_pay && hasPermission('pg-invoices.pay') ? (
              <div className="pg-actions" style={{ marginTop: 16 }}>
                <RTButton
                  variant="primary"
                  onClick={handlePay}
                  disabled={paying}
                >
                  {paying ? 'Please wait…' : 'Pay Now'}
                </RTButton>
              </div>
            ) : null}
          </div>
        )}
      </RTModal>

      {paying ? (
        <RTSpinner
          fullScreen
          message="Preparing payment link…"
          style={{ backgroundColor: 'rgba(255, 255, 255, 0.85)' }}
        />
      ) : null}
    </RTPageLayout>
  );
};

export default PGInvoices;
